/**
 * TagSelect Field Renderer
 *
 * Pill-style tag selection with icons
 * Renders options as rounded pill buttons that can be selected/deselected
 * Supports icons and single or multi-selection (default: multi)
 */

import React from 'react';
import { Label } from '@/components/ui/label';
import * as LucideIcons from 'lucide-react';
import type { FieldRenderer } from '@/MyCode/core';

/**
 * TagSelect Renderer
 * Features:
 * - Pill/tag style buttons
 * - Multi-selection support
 * - Icon support from Lucide icons
 * - Smooth transitions
 * - Accessible with ARIA attributes
 */
export const renderTagSelect: FieldRenderer = ({ field, value, handleChange, isRequired }) => {
    // Multi-select by default; allow single-select when field.ui.multiSelect === false
    const multiSelect = field.ui?.multiSelect !== false;

    // Handle both single and multi-select values
    const selectedValues = multiSelect
        ? (Array.isArray(value) ? value : (value ? [value] : []))
        : [];
    const selectedValue = !multiSelect ? (typeof value === 'string' ? value : '') : '';

    const toggleTag = (itemValue: string) => {
        if (multiSelect) {
            // Multi-select: toggle item in array
            let newValues: string[];
            if (selectedValues.includes(itemValue)) {
                newValues = selectedValues.filter(v => v !== itemValue);
            } else {
                newValues = [...selectedValues, itemValue];
            }
            handleChange(field.name, newValues);
        } else {
            // Single-select: set single value (or clear if clicking same item)
            const newValue = selectedValue === itemValue ? '' : itemValue;
            handleChange(field.name, newValue);
        }
    };

    const options = field.options?.source === 'static' ? field.options.items : [];

    return (
        <div className="space-y-3">
            <Label>
                {field.label}
                {isRequired && <span className="text-destructive ml-1">*</span>}
            </Label>
            {field.description && (
                <p className="text-sm text-muted-foreground">{field.description}</p>
            )}
            <div className="flex flex-wrap gap-3">
                {options.map((option) => {
                    const isSelected = multiSelect
                        ? selectedValues.includes(option.value)
                        : selectedValue === option.value;
                    const IconComponent = option.icon ? LucideIcons[option.icon as keyof typeof LucideIcons] : null;

                    return (
                        <button
                            key={option.value}
                            type="button"
                            onClick={() => toggleTag(option.value)}
                            className={`
                                inline-flex items-center gap-2 px-6 py-3 rounded-full border-2
                                transition-all duration-200 ease-in-out
                                ${isSelected
                                    ? 'border-foreground bg-foreground text-background font-medium'
                                    : 'border-border bg-background text-foreground hover:border-foreground/40'
                                }
                            `}
                            aria-pressed={isSelected}
                        >
                            {IconComponent && (
                                <IconComponent
                                    className={`size-5 ${isSelected ? 'opacity-100' : 'opacity-70'}`}
                                    aria-hidden="true"
                                />
                            )}
                            <span className="text-base">{option.title || option.label}</span>
                        </button>
                    );
                })}
            </div>
        </div>
    );
};

export default renderTagSelect;
